import React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import parsePhoneNumber, { isValidPhoneNumber } from "libphonenumber-js";
import CountriesCodesMapping from "../utils/CountriesCodesMapping";
import { useApi } from "../Api/ApiContext";
import { useAsync, useAsyncFn } from "react-use";
import {
  Col,
  Form,
  FormFeedback,
  FormText,
  InputGroup,
  InputGroupText,
  Row,
  Spinner,
  Alert,
  Button,
  FormGroup,
} from "reactstrap";
import styled from "styled-components";
import Input from "../component/Input";
import Select from "../component/Select";
import useCelebritiesStore from "../store/CelebritiesStore";
import Textarea from "../component/Textarea";

export const BoostrapIcon = styled.i`
  font-size: 1rem !important;
`;

// Les numéros ne sont pas contrôlés : le pays ne sert qu'à afficher un drapeau
// (et à deviner le pays de résidence) quand le numéro est reconnu.
const findPhoneNumberCountry = (value = "") => {
  const phoneNumber = value.startsWith("00") ? `+${value.slice(2)}` : value;

  return isValidPhoneNumber(phoneNumber)
    ? CountriesCodesMapping.find(
        (c) => c.code === parsePhoneNumber(phoneNumber).country
      )
    : undefined;
};

const SignUpForm = ({ user = null }) => {
  const navigate = useNavigate();
  const { signUp, updateUserInfos, getCelebrityCategories } = useApi();
  const [{ loading, error, value }, SignUp] = useAsyncFn((user) =>
    signUp(user)
  );
  const [
    { loading: updateLoading, error: updateError, value: updateResponse },
    updateUser,
  ] = useAsyncFn((user) => updateUserInfos(user));

  const {
    loading: categoriesLoading,
    error: categoriesError,
    value: celebrityCategories,
  } = useAsync(() => getCelebrityCategories(), []);

  const {
    register,
    handleSubmit,
    reset,
    values,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      familyName: user?.familyName || "",
      givenName: user?.givenName || "",
      nickname: user?.nickname || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
      whatsappNumber: user?.whatsappNumber || "",
      locale: user?.locale || "",
      password: "",
      passwordConfirmation: "",
      title: user?.title || "",
      categoryId: user?.category.id || "",
      delay: user?.delay || null,
      currency: user?.currency || "",
      facebook: user?.facebook || "",
      tiktok: user?.tiktok || "",
      instagram: user?.instagram || "",
      twitter: user?.twitter || "",
      linkedin: user?.linkedin || "",
      description: user?.description || "",
      available: user?.available || "",
      supportAssociation: user?.supportAssociation || false,
    },
  });

  const [showPassword, setShowPassword] = React.useState(false);

  const [categoryId, setCategoryId] = React.useState(
    user ? user.category.id : ""
  );

  const [country, setCountry] = React.useState(() =>
    findPhoneNumberCountry(user?.phoneNumber)
  );

  const [whatsappCountry, setWhatsappCountry] = React.useState(() =>
    findPhoneNumberCountry(user?.whatsappNumber)
  );

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handlePhoneNumberChange = (e) => {
    setValue("phoneNumber", e.target.value, { shouldValidate: true });

    // Sans pays reconnu, le pays de résidence se remplit à la main.
    const country = findPhoneNumberCountry(e.target.value);

    setCountry(country);
    if (country) {
      setValue("locale", country.name, { shouldValidate: true });
    }
  };

  const whatsappNumberField = register("whatsappNumber");

  const handleWhatsappNumberChange = (e) => {
    whatsappNumberField.onChange(e);
    setWhatsappCountry(findPhoneNumberCountry(e.target.value));
  };

  const onSubmit = (data) => {
    if (user) {
      updateUser({ ...data, categoryId: parseInt(data.categoryId) });
      return;
    }

    if (data) {
      SignUp({ ...data, categoryId: parseInt(data.categoryId) });
    }
  };

  if (loading || categoriesLoading) {
    return (
      <Row>
        <Col lg={6} className={"mx-auto"}>
          <Spinner color={"danger"} />
        </Col>
      </Row>
    );
  }

  if (value) {
    navigate("/auth/signup-success");
  }

  if (updateResponse) {
    navigate("/auth/update-success");
  }

  return (
    <div className={"my-3 my-lg-3"}>
      <Row>
        <Col lg={6} className={"mx-auto"}>
          <h1 className={"fs-4"}>
            {user ? "Modifier votre profil" : "Créez votre compte"}
          </h1>
        </Col>
      </Row>
      {error && (
        <Row>
          <Col lg={6} className={"mx-auto"}>
            <Alert color="danger">
              Une erreur est survenue. Votre compte n'a pu être créé. Merci de
              réessayer plus tard.
            </Alert>
          </Col>
        </Row>
      )}
      {updateError && (
        <Row>
          <Col lg={6} className={"mx-auto"}>
            <Alert color="danger">
              Une erreur est survenue. Vos informations n'ont pas été mises à
              jour. Merci de réessayer plus tard.
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={6} className={"mx-auto"}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <label htmlFor="family-name" className={"form-label"}>
                Nom de famille <FormText>(obligatoire)</FormText>
              </label>
              <Input
                className={
                  errors.familyName ? "is-invalid form-control" : "form-control"
                }
                type="text"
                id="family-name"
                {...register("familyName", {
                  required: "Ce champ est obligatoire.",
                })}
                name="familyName"
                value={values?.familyName}
              />
              {errors.familyName && (
                <FormFeedback>{errors.familyName.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="given-name" className={"form-label"}>
                Prénom <FormText>(obligatoire)</FormText>
              </label>
              <Input
                className={
                  errors.givenName ? "is-invalid form-control" : "form-control"
                }
                type="text"
                id="given-name"
                name="givenName"
                {...register("givenName", {
                  required: "Ce champ est obligatoire.",
                })}
                value={values?.givenName}
              />
              {errors.givenName && (
                <FormFeedback>{errors.givenName.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="nickname" className={"form-label"}>
                Pseudo
              </label>
              <Input
                className={"form-control"}
                type="text"
                id="nickname"
                name="nickname"
                {...register("nickname")}
                value={values?.nickname}
              />
              <FormText>
                Ce pseudo sera affiché aux utilisateurs afin de vous identifer.
                Si vous souhaitez être identifié par vos nom et prénom, laissez
                ce champ vide.
              </FormText>
            </FormGroup>
            {!user && (
              <>
                <FormGroup>
                  <label htmlFor="email" className={"form-label"}>
                    Adresse email <FormText>(obligatoire)</FormText>
                  </label>
                  <Input
                    className={
                      errors.email ? "is-invalid form-control" : "form-control"
                    }
                    type="email"
                    id="email"
                    name="email"
                    {...register("email", {
                      required: "Ce champ est obligatoire.",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message:
                          "Le format de l'adresse e-mail n'est pas valide.",
                      },
                    })}
                    value={values?.email}
                  />
                  {errors.email && (
                    <FormFeedback>{errors.email.message}</FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <label htmlFor="password" className={"form-label"}>
                    Mot de passe <FormText>(obligatoire)</FormText>
                  </label>
                  <InputGroup>
                    <Input
                      className={
                        errors.password
                          ? "is-invalid form-control"
                          : "form-control"
                      }
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      {...register("password", {
                        required: "Ce champ est obligatoire.",
                        pattern: {
                          value:
                            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                          message: "Le format de mot de passe n'est pas valide",
                        },
                      })}
                      value={values?.password}
                    />

                    <InputGroupText>
                      {showPassword ? (
                        <BoostrapIcon
                          className="fa-solid fa-eye fs-4"
                          onClick={handleShowPassword}
                        />
                      ) : (
                        <BoostrapIcon
                          className="fa-solid fa-eye-slash fs-4"
                          onClick={handleShowPassword}
                        />
                      )}
                    </InputGroupText>
                  </InputGroup>
                  {errors.password && (
                    <div>
                      <small className={"text-danger"}>
                        {errors.password.message}
                      </small>
                    </div>
                  )}

                  <FormText>
                    Le mot de passe doit contenir au moins 8 caractères dont une
                    lettre majuscule, une lettre minuscule, un chiffre et un
                    caractère spécial parmi ceux-ci{" "}
                    <span className={"fw-bold"}>@ $ ! % * ? &</span>
                  </FormText>
                </FormGroup>
                <FormGroup>
                  <label
                    htmlFor="password-confirmation"
                    className={"form-label"}
                  >
                    Confirmer le mot de passe <FormText>(obligatoire)</FormText>
                  </label>
                  <InputGroup>
                    <Input
                      className={
                        errors.passwordConfirmation
                          ? "is-invalid form-control"
                          : "form-control"
                      }
                      type={showPassword ? "text" : "password"}
                      id="password-confirmation"
                      name={"passwordConfirmation"}
                      {...register("passwordConfirmation", {
                        validate: (value, formValues) =>
                          value === formValues.password,
                      })}
                    />
                    <InputGroupText>
                      {showPassword ? (
                        <BoostrapIcon
                          className="fa-solid fa-eye fs-4"
                          onClick={handleShowPassword}
                        />
                      ) : (
                        <BoostrapIcon
                          className="fa-solid fa-eye-slash fs-4"
                          onClick={handleShowPassword}
                        />
                      )}
                    </InputGroupText>
                  </InputGroup>
                  {errors.passwordConfirmation && (
                    <div>
                      <small className={"text-danger"}>
                        Les deux mots de passe doivent être identiques.
                      </small>
                    </div>
                  )}
                </FormGroup>
              </>
            )}
            <FormGroup>
              <label htmlFor="phone-number" className={"form-label"}>
                Numero de téléphone - paiement mobile{" "}
                <FormText>(obligatoire)</FormText>
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.phoneNumber
                      ? "is-invalid form-control"
                      : "form-control"
                  }
                  type={"text"}
                  id="phone-number"
                  name={"phoneNumber"}
                  {...register("phoneNumber", {
                    required: "Ce champ est obligatoire.",
                  })}
                  placeholder="+3301010101"
                  value={values?.phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e)}
                />
                {country && <InputGroupText>{country.emoji}</InputGroupText>}
              </InputGroup>
              {errors.phoneNumber && (
                <div>
                  <small className={"text-danger"}>
                    {errors.phoneNumber.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="whatsapp-number" className={"form-label"}>
                Numero de contact (whatsapp)
              </label>
              <InputGroup>
                <Input
                  className={"form-control"}
                  type={"text"}
                  id="whatsapp-number"
                  name={"whatsappNumber"}
                  {...whatsappNumberField}
                  placeholder="+3301010101"
                  value={values?.whatsappNumber}
                  onChange={(e) => handleWhatsappNumberChange(e)}
                />
                {whatsappCountry && (
                  <InputGroupText>{whatsappCountry.emoji}</InputGroupText>
                )}
              </InputGroup>
            </FormGroup>
            <FormGroup>
              <label htmlFor="locale" className={"form-label"}>
                Pays de résidence <FormText>(obligatoire)</FormText>
              </label>
              <Input
                className={
                  errors.locale ? "is-invalid form-control" : "form-control"
                }
                type={"text"}
                id="locale"
                name={"locale"}
                {...register("locale", {
                  required: "Ce champ est obligatoire.",
                })}
                value={values?.local}
              />
              {errors.locale && (
                <FormFeedback>{errors.locale.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="title" className={"form-label"}>
                Profession <FormText>(obligatoire)</FormText>
              </label>
              <Input
                className={
                  errors.title ? "is-invalid form-control" : "form-control"
                }
                type={"text"}
                id="title"
                name={"title"}
                {...register("title", {
                  required: "Ce champ est obligatoire.",
                })}
                value={values?.title}
              />
              {errors.title && (
                <FormFeedback>{errors.title.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="categoryId" className={"form-label"}>
                Catégorie de profession <FormText>(obligatoire)</FormText>
              </label>
              <Select
                className={
                  errors.categoryId ? "is-invalid form-control" : "form-control"
                }
                id="categoryId"
                name="categoryId"
                {...register("categoryId", {
                  required: "Ce champ est obligatoire.",
                })}
                value={values?.categoryId}
              >
                <option value={""}>selection</option>
                {celebrityCategories.map((c, index) => (
                  <option
                    key={index}
                    selected={c.id === categoryId}
                    value={c.id}
                  >
                    {c.name}
                  </option>
                ))}
              </Select>
              {errors.categoryId && (
                <FormFeedback>{errors.categoryId.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="delay" className={"form-lable"}>
                Délai de livraison <FormText>(obligatoire)</FormText>
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.delay ? "is-invalid form-control" : "form-control"
                  }
                  id={"delay"}
                  type={"number"}
                  name={"delay"}
                  {...register("delay", {
                    required: "Ce champ est obligatoire.",
                  })}
                  value={values?.delay}
                  min={"24"}
                  max={"120"}
                  step={"24"}
                />
                <InputGroupText>heures</InputGroupText>
              </InputGroup>
              {errors.delay && (
                <div>
                  <small className={"text-danger"}>
                    {errors.delay.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="currency" className={"form-label"}>
                Devise de paiement <FormText>(obligatoire)</FormText>
              </label>
              <Select
                className={
                  errors.currency ? "is-invalid form-control" : "form-control"
                }
                id="currency"
                name="currency"
                {...register("currency", {
                  required: "Ce champ est obligatoire.",
                })}
                value={values?.currency}
              >
                <option selected value={""}>
                  Sélection
                </option>
                <option value={"cfa"}>CFA</option>
                <option value={"euro"}>EURO</option>
              </Select>
              {errors.currency && (
                <FormFeedback>{errors.currency.message}</FormFeedback>
              )}
              <FormText>
                Choisissez la devise dans laquelle vous souhaitez fixer le prix
                de vos prestations.
              </FormText>
            </FormGroup>
            <FormGroup>
              <label htmlFor={"facebook"} className={"form-label"}>
                Page facebook
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.facebook ? "is-invalid form-control" : "form-control"
                  }
                  type={"text"}
                  id={"facebook"}
                  value={values?.facebook}
                  name={"facebook"}
                  {...register("facebook", {
                    pattern: {
                      value:
                        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#@!$&'()*+,;=]*)*\/?$/,
                      message: "Le format de l'url n'est pas valide.",
                    },
                  })}
                />

                <InputGroupText>
                  <i className="fa-brands fa-facebook fs-4" />
                </InputGroupText>
              </InputGroup>
              {errors.facebook && (
                <div>
                  <small className="text-danger">
                    {errors.facebook.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor={"tiktok"} className={"form-label"}>
                Page TikTok
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.tiktok ? "is-invalid form-control" : "form-control"
                  }
                  type={"text"}
                  id={"tiktok"}
                  value={values?.tiktok}
                  name={"facebook"}
                  {...register("tiktok", {
                    pattern: {
                      value:
                        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#@!$&'()*+,;=]*)*\/?$/,
                      message: "Le format de l'url n'est pas valide.",
                    },
                  })}
                />

                <InputGroupText>
                  <i className="fa-brands fa-tiktok fs-4" />
                </InputGroupText>
              </InputGroup>
              {errors.tiktok && (
                <div>
                  <small className="text-danger">{errors.tiktok.message}</small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor={"instagram"} className={"form-label"}>
                Page instagram
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.instagram
                      ? "is-invalid form-control"
                      : "form-control"
                  }
                  type={"text"}
                  id={"instagram"}
                  value={values?.instagram}
                  name={"instagram"}
                  {...register("instagram", {
                    pattern: {
                      value:
                        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#@!$&'()*+,;=]*)*\/?$/,
                      message: "Le format de l'url n'est pas valide.",
                    },
                  })}
                />
                <InputGroupText>
                  <i className="fa-brands fa-square-instagram fs-4" />
                </InputGroupText>
              </InputGroup>
              {errors.instagram && (
                <div>
                  <small className="text-danger">
                    {errors.instagram.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor={"twitter"} className={"form-label"}>
                Page X (ex twitter)
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.twitter ? "is-invalid form-control" : "form-control"
                  }
                  type={"text"}
                  id={"twitter"}
                  value={values?.twitter}
                  name={"twitter"}
                  {...register("twitter", {
                    pattern: {
                      value:
                        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#@!$&'()*+,;=]*)*\/?$/,
                      message: "Le format de l'url n'est pas valide.",
                    },
                  })}
                />
                <InputGroupText>
                  <i className="fa-brands fa-square-x-twitter fs-4" />
                </InputGroupText>
              </InputGroup>
              {errors.twitter && (
                <div>
                  <small className="text-danger">
                    {errors.twitter.message}
                  </small>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <label htmlFor={"linkedin"} className={"form-label"}>
                Page linkedin
              </label>
              <InputGroup>
                <Input
                  className={
                    errors.linkedin ? "is-invalid form-control" : "form-control"
                  }
                  type={"text"}
                  id={"linkedin"}
                  value={values?.linkedin}
                  name={"linkedin"}
                  {...register("linkedin", {
                    pattern: {
                      value:
                        /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:?#@!$&'()*+,;=]*)*\/?$/,
                      message: "Le format de l'url n'est pas valide.",
                    },
                  })}
                />
                <InputGroupText>
                  <i className="fa-brands fa-linkedin fs-4" />
                </InputGroupText>
              </InputGroup>
              {errors.linkedin && (
                <div>
                  <small className="text-danger">
                    {errors.linkedin.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <label htmlFor="description" className={"form-label"}>
                Décrivez-vous en quelques mots{" "}
                <FormText>(obligatoire)</FormText>
              </label>
              <Textarea
                className={
                  errors.description
                    ? "is-invalid form-control"
                    : "form-control"
                }
                id="description"
                name="description"
                rows={5}
                maxLength={500}
                value={values?.description}
                type="textarea"
                {...register("description", {
                  required: "Ce champ est obligatoire.",
                })}
              />
              {errors.description && (
                <div>
                  <small className={"text-danger"}>
                    {errors.description.message}
                  </small>
                </div>
              )}
            </FormGroup>
            <FormGroup row>
              <Col xs={1}>
                <Input
                  id={"supportAssociation"}
                  name={"supportAssociation"}
                  type={"checkbox"}
                  {...register("supportAssociation")}
                  checked={values?.supportAssociation}
                />
              </Col>
              <Col>
                <label htmlFor={"supportAssociation"} className={"form-label"}>
                  Je soutiens une association
                </label>
              </Col>
            </FormGroup>
            <FormGroup>
              <label className="form-label">
                Êtes-vous actuellement disponible pour faire des vidéos ?{" "}
                <FormText>(obligatoire)</FormText>
              </label>
              <FormGroup row>
                <Col xs={1}>
                  <Input
                    className={errors.available && "is-invalid"}
                    id={"available-yes"}
                    name="available"
                    type="radio"
                    value={"yes"}
                    {...register("available", {
                      required: "Ce champ est obligatoire.",
                    })}
                  />
                </Col>
                <Col>
                  <label htmlFor="available-yes" className={"form-label"}>
                    Oui je suis disponible
                  </label>
                </Col>
              </FormGroup>
              <FormGroup row>
                <Col xs={1}>
                  <Input
                    className={errors.available && "is-invalid"}
                    id={"available-no"}
                    name="available"
                    type="radio"
                    {...register("available", {
                      required: "Ce champ est obligatoire.",
                    })}
                    value={"no"}
                  />
                </Col>
                <Col>
                  <label htmlFor="available-no" className={""}>
                    Non je ne suis pas disponible
                  </label>
                </Col>
              </FormGroup>
              {errors.available && (
                <div>
                  <small className={"text-danger"}>
                    {errors.available.message}
                  </small>
                </div>
              )}
            </FormGroup>
            {!user && (
              <FormGroup row>
                <Col xs={1}>
                  <Input
                    className={errors.acceptTerms && "is-invalid"}
                    id={"accept-terms"}
                    name={"acceptTerms"}
                    type={"checkbox"}
                    {...register("acceptTerms", {
                      required:
                        "Vous devez accepter les conditions générales d'utilisation.",
                    })}
                  />
                </Col>
                <Col>
                  <label htmlFor={"accept-terms"} className={"form-label"}>
                    <Link
                      to={"/terms"}
                      target={"_blank"}
                      rel={"noopener noreferrer"}
                    >
                      Conditions générales d'utilisation
                    </Link>{" "}
                    <FormText>(obligatoire)</FormText>
                  </label>
                  {errors.acceptTerms && (
                    <div>
                      <small className={"text-danger"}>
                        {errors.acceptTerms.message}
                      </small>
                    </div>
                  )}
                </Col>
              </FormGroup>
            )}
            <FormGroup
              className={"justify-content-sm-end justify-content-around"}
              row
            >
              <Col xs={6} sm={3}>
                <Button
                  className={"w-100"}
                  onClick={() => reset((formValues) => {})}
                >
                  Annuler
                </Button>
              </Col>
              <Col xs={6} sm={3}>
                <Button className={"w-100"} color={"primary"} type={"submit"}>
                  Enregistrer
                </Button>
              </Col>
            </FormGroup>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default SignUpForm;
